import { config } from '@app/config';
import { growthbook } from '@app/core/integrations/growthbook';
import { socialExport } from '@mi-front/api-clients';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import StoreIcon from '@mui/icons-material/Store';
import XIcon from '@mui/icons-material/X';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { SvgIconProps } from '@mui/material';
import React from 'react';
import { DailymotionIcon } from '../platforms/dailymotion/DailymotionIcon';
import { TikTokIcon } from '../platforms/tiktok/components/TiktokIcon';
export const SocialPlatformEnum = socialExport.UploadDtoPlatformEnum;

export type SocialPlatformId = (typeof SocialPlatformEnum)[keyof typeof SocialPlatformEnum];

export type SocialPlatformIconProps = SvgIconProps & {
  /** When true, applies the platform's main brand color to the icon. */
  colored?: boolean;
};

export class SocialPlatform {
  constructor(
    readonly id: SocialPlatformId,
    readonly name: string,
    readonly _icon: React.ReactElement,
    /** Main brand color (e.g. hex) for the platform. Used when Icon({ colored: true }). */
    readonly color?: string,
  ) {}

  Icon(props?: SocialPlatformIconProps) {
    const { colored, sx, ...rest } = props ?? {};
    const mergedSx = colored && this.color ? { ...sx, color: this.color } : sx;
    return React.cloneElement(this._icon, {
      ...rest,
      sx: mergedSx,
    });
  }
}

export const YoutubePlatform = new SocialPlatform(
  SocialPlatformEnum.YOUTUBE,
  'YouTube',
  <YouTubeIcon />,
  '#FF0000',
);

export const TwitterPlatform = new SocialPlatform(
  SocialPlatformEnum.TWITTER,
  'X',
  <XIcon />,
  '#000000',
);

export const InstagramPlatform = new SocialPlatform(
  SocialPlatformEnum.INSTAGRAM,
  'Instagram',
  <InstagramIcon />,
  '#E4405F',
);

export const DailymotionPlatform = new SocialPlatform(
  SocialPlatformEnum.DAILYMOTION,
  'Dailymotion',
  <DailymotionIcon />,
  '#0d0d0d',
);

export const FacebookPlatform = new SocialPlatform(
  SocialPlatformEnum.FACEBOOK,
  'Facebook',
  <FacebookIcon />,
  '#1877F2',
);

export const TikTokPlatform = new SocialPlatform(
  SocialPlatformEnum.TIKTOK,
  'TikTok',
  <TikTokIcon />,
  '#000000',
);

export const DemoPlatform = new SocialPlatform(
  SocialPlatformEnum.DEMO,
  'Demo',
  <StoreIcon />,
  '#757575',
);

export class SocialPlatforms {
  private readonly basePlatforms = [
    YoutubePlatform,
    TwitterPlatform,
    InstagramPlatform,
    DailymotionPlatform,
    FacebookPlatform,
    TikTokPlatform,
  ];

  /** All registered platforms, sorted by display name. */
  all() {
    const isDemoEnabled =
      config.isNodeDev || growthbook.isOn('app__social_export_demo_platform_enabled');

    return [...this.basePlatforms, ...(isDemoEnabled ? [DemoPlatform] : [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }

  get(id: string) {
    const platform = this.all().find((p) => p.id.toLowerCase() === id.toLowerCase());

    if (!platform) {
      throw new Error(`Platform ${id} not found`);
    }

    return platform;
  }
}

export const socialPlatforms = new SocialPlatforms();
